import { ContentTypeFileIcon, FilmStripIcon } from '@/components/common/icon';
import { useCallback, useEffect, useRef, useState } from 'react';
import CreatorQuoteDesignIcon from '@/components/multiPlatformCollaboration/CreatorQuoteDesignIcon';

export type CreatorContentTypeOption = {
  value: string;
  label: string;
  description: string;
  price?: number;
  icon: 'film' | 'file';
};

export type CreatorContentTypeSelectProps = {
  value: string;
  options: CreatorContentTypeOption[];
  disabled?: boolean;
  active?: boolean;
  onChange: (option: CreatorContentTypeOption) => void;
  onOpenChange?: (open: boolean) => void;
};

export default function CreatorContentTypeSelect({
  value,
  options,
  disabled = false,
  active = true,
  onChange,
  onOpenChange
}: CreatorContentTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected =
    options.find((option) => option.value === value) ?? options[0];
  const hasMultipleOptions = options.length > 1;
  const updateOpen = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if ((disabled || !active) && open) updateOpen(false);
  }, [disabled, active, open, updateOpen]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) updateOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [open, updateOpen]);

  if (!selected) return null;
  if (!hasMultipleOptions || disabled) {
    return (
      <span className="min-w-0 truncate whitespace-nowrap font-['Inter'] text-base font-semibold leading-6 text-[#1C1917]">
        {selected.label}
      </span>
    );
  }

  const SelectedIcon =
    selected.icon === 'file' ? ContentTypeFileIcon : FilmStripIcon;
  return (
    <div ref={rootRef} className="relative w-60 max-md:w-full">
      <button
        type="button"
        className="flex h-9 w-full items-center gap-1.5 rounded-lg border border-[#D4D4D4] bg-white px-2.5 text-sm font-medium leading-5 text-[#171717] shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => updateOpen(!open)}
      >
        <SelectedIcon className="h-[18px] w-[18px] flex-shrink-0 text-[#404040]" />
        <span className="min-w-0 flex-1 truncate text-left">
          {selected.label}
        </span>
        <CreatorQuoteDesignIcon
          name="chevronDown"
          className={`h-4 w-4 flex-shrink-0 text-[#737373] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-[44px] z-40 w-[268px] overflow-hidden rounded-lg border border-black/10 bg-white p-4 shadow-[0_12px_16px_-4px_rgba(0,0,0,0.08),0_4px_6px_-2px_rgba(0,0,0,0.03),0_2px_2px_-1px_rgba(0,0,0,0.04)] max-md:w-full"
        >
          {options.map((option, index) => (
            <div key={option.value}>
              {index ? <div className="my-4 h-px bg-[#E5E5E5]" /> : null}
              <button
                type="button"
                role="menuitemradio"
                aria-checked={option.value === selected.value}
                className="block w-full border-0 bg-white p-0 text-left"
                onClick={() => {
                  onChange(option);
                  updateOpen(false);
                }}
              >
                <span className="flex items-center gap-1 text-sm font-semibold leading-5 text-[#171717]">
                  {option.icon === 'file' ? (
                    <ContentTypeFileIcon className="h-[18px] w-[18px] flex-shrink-0 text-[#404040]" />
                  ) : (
                    <FilmStripIcon className="h-[18px] w-[18px] flex-shrink-0 text-[#404040]" />
                  )}
                  {option.label}
                </span>
                <span className="mt-1 block text-sm font-normal leading-5 text-[#737373]">
                  {option.description}
                </span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
